import environments from '@app/configs/environments';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { Mutex } from 'async-mutex';
import Router from 'next/router';

// create a new mutex
const mutex = new Mutex();

const baseQuery = fetchBaseQuery({
    baseUrl: environments.CLIENT_SIDE_ENDPOINT_HOST + '/api',
    credentials: 'include',
    prepareHeaders(headers, api) {
        const isMultipartEndpoint = api.endpoint === 'uploadPdf' || api.endpoint === 'resumeDocumentProcess' || api.endpoint === 'putBlacklist' || api.endpoint === 'putWhitelist';

        if (!isMultipartEndpoint) {
            headers.set('Content-Type', 'application/json');
        }

        return headers;
    }
});

export const customBaseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
    await mutex.waitForUnlock();
    let result = await baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
        if (!mutex.isLocked()) {
            const release = await mutex.acquire();
            try {
                const refreshResult = await fetch(`${environments.CLIENT_SIDE_ENDPOINT_HOST}/api/oauth/refreshToken`, {
                    method: 'POST',
                    credentials: 'include'
                });

                if (refreshResult.status >= 200 && refreshResult.status < 300) {
                    // Retry the original request
                    result = await baseQuery(args, api, extraOptions);
                } else {
                    // Redirect logic (only on client side)
                    if (typeof window !== 'undefined') {
                        const pathname = window.location.pathname;
                        const alreadyRedirected = sessionStorage.getItem('redirected');
                        console.log('already redirected', alreadyRedirected);

                        const isSafePath = pathname === '/' || pathname.startsWith('/oauth') || window.location.pathname === '/contact-us';

                        if (!isSafePath && !alreadyRedirected) {
                            sessionStorage.setItem('redirected', 'true');
                        } else {
                            sessionStorage.removeItem('redirected');
                        }
                        await Router.replace('/');
                    }
                }
            } finally {
                // release must be called once the mutex should be released again.
                release();
            }
        } else {
            // wait until the mutex is available without locking it
            await mutex.waitForUnlock();
            result = await baseQuery(args, api, extraOptions);
        }
    }

    return result;
};
